import { useTranslation } from 'next-i18next'
// Libs
import { useState, useContext, useMemo, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { useRouter } from 'next/router'

// Comps
import { CTA } from '../../CTA'
import { EventHookInterface, ToastType } from '../../../types'
import { show, showFinally, prependHttpsToUrl, colors } from '../../../utils'
import { EmailRSVPEventModal } from '../../Modals/EmailRSVPEventModal'
import { useEmailRsvp } from '../../../hooks/emailRsvp'
import { EmailRsvpProcessPaymentModal } from '../../Modals/EmailRsvpProcessPaymentModal'
import { ShareEventModal } from '../../Modals/ShareEventModal'
import { AddToCalendarModal } from '../../Modals/AddToCalendarModal'
import { PaymentSuccessModal } from '../../Modals/PaymentSuccessModal'
import { P } from '../../Headings'
import { BookButton } from './BookButton'
import { EmailRSVPWaitingListButton } from './EmailRSVPWaitingListButton'
import { EmailRSVPEventWaitingListModal } from '../../Modals/EmailRSVPEventWaitingListModal'
import { Hyperlink } from '../../Hyperlink'
import { ToastContext, AnalyticsContext, UserContext } from '../../../context'
import { useAnalyticsEvent } from '../../../hooks/analyticsEvent'
import { useSsrLocalStorage } from '../../../hooks/useLocalStorage'

const ButtonBoxContainer = styled.div`
  .buttonDropdownContainer {
    position: absolute;
    display: flex;
    flex-direction: column;
    padding: 13px 10px;
    width: 234px;
    background: #ffffff;
    box-shadow: 0px 10px 15px rgba(0, 0, 0, 0.1);
    border-radius: 6px;

    button {
      cursor: pointer;
      display: flex;

      align-items: center;
      padding: 14px;
      background: none;
      border: none;
      border-radius: 6px;
      outline: none;
      font-size: 16px;
      font-weight: 600;
      letter-spacing: -0.5px;
      line-height: 110%;
      text-align: left;
      transition: 150ms ease-in-out background;

      &:hover {
        background-color: #f0edfd;
        svg.circle circle {
          &:not(:first-of-type) {
            stroke: ${colors.grey};
            fill: ${colors.grey};
          }
        }
      }

      svg.star {
        margin-right: 5px;
        path {
          stroke: ${colors.grey};
          fill: ${colors.grey};
        }
      }

      svg.remove {
        margin-right: 5px;
        path,
        circle {
          stroke: ${colors.grey};
        }
      }

      svg.circle {
        margin-left: auto;
        circle {
          stroke: ${colors.grey};
          &:not(:first-of-type) {
            stroke: transparent;
            fill: transparent;
          }
        }
      }
      &.buttonIsActive {
        color: ${colors.purple};
        svg.star path {
          stroke: ${colors.purple};
          fill: ${colors.purple};
        }

        svg.remove {
          path,
          circle {
            stroke: ${colors.purple};
          }
        }

        svg.circle circle {
          stroke: ${colors.purple};
          &:not(:first-of-type) {
            stroke: ${colors.purple};
            fill: ${colors.purple};
          }
        }
      }
    }
  }

  & .joinMeeting {
    & button {
      width: 238px;
    }
  }
`

// Defs
interface Props {
  eventHook: EventHookInterface
  handleNotGoing: () => void
}

export const EmailRSVPButton: React.FC<Props> = ({
  eventHook,
  handleNotGoing,
}) => {
  const { t } = useTranslation('common')

  // Local State
  const [showFormModal, setShowFromModal] = useState(false)
  const [showStripModal, setShowStripeModal] = useState(false)
  const [showFreeSuccessModal, setShowFreeSuccessModal] = useState(false)
  const [showEventCalendarModal, setShowEventCalendarModal] = useState(false)
  const [showPaymentSuccessModal, setShowPaymentSuccessModal] = useState(false)
  const [showPaymentSuccessConfirmation, setShowPaymentSuccessConfirmation] =
    useState(false)
  const [showWaitingListModal, setShowWaitingListModal] = useState(false)

  const dropdownRef = useRef<HTMLDivElement>(null)
  const [showButtonDropdown, setShowButtonDropdown] = useState(false)

  // Global State
  const analyticsContext = useContext(AnalyticsContext)
  const { currentUser } = useContext(UserContext)

  // Shared State
  const analyticEvent = useAnalyticsEvent()
  const router = useRouter()
  const accessToken: string = (router?.query?.ut as string) || undefined
  const toastContext = useContext(ToastContext)
  const emailRsvpHock = useEmailRsvp({
    eventId: eventHook.value.id,
    accessToken: accessToken,
  })
  const {
    goToFreeEvent,
    goToPayEventCreateUser,
    reset,
    currentEvent,
    currentEventButtonLabelData,
    unGoToEvent,
    isLoading,
    joinWaitingList,
    leaveWaitingList,
    currentEventTimeData,
  } = emailRsvpHock
  const { video_url } = currentEvent || {}

  const [, setRedirect] = useSsrLocalStorage('redirect', '')

  // Effects
  // On page load then then allow user to dismiss the drop drop down
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowButtonDropdown(false)
      }
    }
    // Add Event Listener — Click.
    window && window.addEventListener('click', handleClickOutside)
    // Tear Down.
    return () =>
      window && window.removeEventListener('click', handleClickOutside)
  }, [dropdownRef])

  // Calc
  const isStillCurrentEvent =
    currentEventTimeData.isActive === true ? true : false

  // is the user going to the event
  const isParticipant = currentEvent?.is_user_going ? true : false

  // has the event started yet = show link
  const showJoinVideoLink =
    currentEventTimeData.isInside15mins === true ||
    (currentEventTimeData.alreadyStarted === true &&
      currentEventTimeData.isNotFinished === true)
      ? true
      : false

  // Build video link
  const videoLink = useMemo(() => {
    if (video_url) {
      return prependHttpsToUrl(video_url)
    }
    return ''
  }, [video_url])

  // is the event fully booked
  const showFullyBooked =
    eventHook.value.group_size !== null &&
    eventHook.value.participants_number >= eventHook.value.group_size
      ? true
      : false
  // Funcs
  // Submit paid or free event
  const onSubmited = async () => {
    // Handel Free
    if (eventHook.isPaidEvent === false) {
      // Submit user data
      const wasAdded = await goToFreeEvent()
      // Close modal on success
      if (wasAdded == true) {
        // analyticEvent.express.expressEventsRegisterForEvent(event_paid_type?.toString())
        // analyticEvent.fbLead()
        setShowFreeSuccessModal(true)
        setShowFromModal(false)
      }
    }

    if (eventHook.isPaidEvent === true) {
      // Create User
      const wasCreated = await goToPayEventCreateUser()

      // Show next modal on success
      if (wasCreated === true) {
        setShowStripeModal(true)
      }
    }
  }

  // Funcs
  const onFreeDone = () => {
    setShowFreeSuccessModal(false)
  }
  const onFreeAddToCalendarOpen = () => {
    setShowEventCalendarModal(true)
  }
  const onFreeAddToCalendarClose = () => {
    setShowEventCalendarModal(false)
  }
  const onStripeModalCloseBack = () => {
    setShowStripeModal(false)
  }

  const onStripeModalCloseSuccess = () => {
    setShowFromModal(false)
    setShowStripeModal(false)
    setShowPaymentSuccessModal(true)
    // Make sure to reset values
    reset()
  }

  const onStripeModalClose = (showPaymentSuccess = false) => {
    // Handel user exited modal
    if (showPaymentSuccess !== true) {
      onStripeModalCloseBack()
    }
    // On success of payment close and show next modal
    if (showPaymentSuccess === true) {
      // Close Modal and show success
      // analyticEvent.express.expressEventsRegisterForEvent(event_paid_type?.toString())
      // analyticEvent.fbLead()
      onStripeModalCloseSuccess()
    }
  }

  const onGoingStart = () => {
    if (currentEvent?.is_user_going !== true) {
      setShowFromModal(true)
    }
    setShowButtonDropdown(false)
  }

  const onNotGoingEvent = async () => {
    if (currentEvent?.is_user_going === true) {
      const success = await unGoToEvent()
      // if (success === true) {
      //   // analyticEvent.eventsDeRegisterForEvent()
      // }
    }
    setShowButtonDropdown(false)
  }

  const onJoinWaitingListSubmit = async () => {
    const success = await joinWaitingList()
    if (success === true) {
      setShowWaitingListModal(false)
      toastContext.addToast({
        id: 'waiting list',
        toastType: ToastType.Success,
        title: t(
          `EmailRSVPButton-20220106171024021070487`,
          `You're on the list!  👋`,
        ),
        content: (
          <>
            <br />
            {t(
              `EmailRSVPButton-20220106171039008175476`,
              `We'll notify you via email if a spot opens up.`,
            )}
          </>
        ),
      })
    }
  }

  const onLeaveWaitingList = () => {
    setShowButtonDropdown(false)
    leaveWaitingList()
  }

  const onGoingDropDownClick = () => {
    // If user is logged
    if (currentUser) {
      // If we are already going then show drop down
      if (currentEvent?.is_user_going === true) {
        setShowButtonDropdown(true)
        // If not going just show the modal
      } else {
        analyticEvent.express.expressIntentToAttend()
        analyticsContext.express.expressIntentToAttend({})
        onGoingStart()
      }
      // If not - going to register page
    } else {
      setRedirect(`events/${eventHook.value.id}`)
      router.replace('/signup')
    }
  }

  // ..
  return (
    <ButtonBoxContainer>
      {/* -------------------------- */}
      {/* Show the link to the video if it is about to start or has started */}
      {/* -------------------------- */}

      {show(isParticipant === true && showJoinVideoLink === true) && (
        <>
          <Hyperlink className="joinMeeting" newTab={true} href={videoLink}>
            <CTA className={`bookButton green`}>
              {t(`EmailRSVPButton1858259213`, `Join event now!`)}
            </CTA>
          </Hyperlink>
        </>
      )}

      {/* Show the join or waiting list button */}
      {showFinally(
        showJoinVideoLink !== true && isStillCurrentEvent === true,
      ) && (
        <>
          {/* Join Button */}
          {show(showFullyBooked === false || isParticipant === true) && (
            <>
              <BookButton
                eventHook={eventHook}
                handleNotGoing={handleNotGoing}
              />
            </>
          )}

          {/* Waiting List button */}
          {showFinally(
            showFullyBooked === true &&
              isParticipant === false &&
              isStillCurrentEvent === true,
          ) && (
            <>
              <EmailRSVPWaitingListButton
                emailRsvpHock={emailRsvpHock}
                onAddToWaitingList={() => {
                  setShowWaitingListModal(true)
                }}
                onRemoveFromWaitingList={onLeaveWaitingList}
              />
            </>
          )}
        </>
      )}

      {/* -------------------------- */}
      {/* Modals */}
      {/* -------------------------- */}

      {show(showStripModal === true) && (
        <>
          <EmailRsvpProcessPaymentModal
            eventHook={eventHook}
            closeModal={onStripeModalClose}
            emailRsvpHock={emailRsvpHock}
          />
        </>
      )}

      {show(showFormModal === true) && (
        <>
          <EmailRSVPEventModal
            onClose={() => setShowFromModal(false)}
            onSubmited={() => {
              onSubmited()
            }}
            emailRsvpHock={emailRsvpHock}
          />
        </>
      )}

      {show(showFreeSuccessModal === true) && (
        <>
          <ShareEventModal
            header={t(
              `EmailRSVPButton-20220106171118249196301`,
              `Success! Your attendance has been confirmed`,
            )}
            text={
              <>
                <P>
                  {t(
                    `EmailRSVPButton-1649740176`,
                    `We'll send you a reminder email 24hrs and 15mins from the event start time.`,
                  )}
                </P>
                <P>
                  {t(
                    `EmailRSVPButton-1048878959`,
                    `To help remember the date we also recommend adding it to your personal calendar:`,
                  )}
                </P>
              </>
            }
            showCalendarLink={true}
            close={onFreeDone}
            showCalendarClick={onFreeAddToCalendarOpen}
            eventHook={eventHook}
          />
        </>
      )}

      {/* Event Calendar Modal. */}
      {showEventCalendarModal && (
        <>
          <AddToCalendarModal
            close={onFreeAddToCalendarClose}
            eventHook={eventHook}
            source="View Event Modal"
            emailRsvpHock={emailRsvpHock}
          />
        </>
      )}

      {showPaymentSuccessModal && (
        <>
          <PaymentSuccessModal
            showCalendarClick={() => setShowEventCalendarModal(true)}
            showPaymentSuccessNotification={showPaymentSuccessConfirmation}
            closePaymentSuccessNotification={() =>
              setShowPaymentSuccessConfirmation(false)
            }
            close={() => setShowPaymentSuccessModal(false)}
          />
        </>
      )}

      {show(showWaitingListModal) && (
        <>
          <>
            <EmailRSVPEventWaitingListModal
              emailRsvpHock={emailRsvpHock}
              onClose={() => {
                setShowWaitingListModal(false)
              }}
              onSubmited={() => {
                onJoinWaitingListSubmit()
              }}
            />
          </>
        </>
      )}
    </ButtonBoxContainer>
  )
}
